import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:5000/api');
const BASE = `${API_BASE_URL}/analytics`;

// Saves a CSV response as a download, using the server's filename.
async function download(path, params, fallbackName) {
  const response = await axios.get(`${BASE}${path}`, {
    params,
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] || '';
  const name = disposition.match(/filename="([^"]+)"/)?.[1] || fallbackName;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

// Blob responses hide the JSON error body; read it back for the message.
export async function downloadErrorMessage(error, fallback) {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return data?.error || fallback;
}

const AnalyticsService = {
  async summary(rangeQuery, basis) {
    return (await axios.get(`${BASE}/summary`, { params: { ...rangeQuery, basis } })).data;
  },
  async channel(id, rangeQuery) {
    return (await axios.get(`${BASE}/channels/${id}`, { params: rangeQuery })).data;
  },
  async topVideos(id, rangeQuery, sort) {
    return (
      await axios.get(`${BASE}/channels/${id}/top-videos`, {
        params: { ...rangeQuery, sort, limit: 10 },
      })
    ).data;
  },
  async recentVideos(id, limit) {
    return (await axios.get(`${BASE}/channels/${id}/recent-videos`, { params: { limit } })).data;
  },
  downloadMetrics(id, from, to) {
    return download(`/channels/${id}/metrics.csv`, { from, to }, 'daily_metrics.csv');
  },
  downloadComments(id, params) {
    return download(`/channels/${id}/comments.csv`, params, 'comments.csv');
  },
  async updateChannel(id, changes) {
    return (await axios.patch(`${BASE}/channels/${id}`, changes)).data;
  },
  async removeChannel(id) {
    await axios.delete(`${BASE}/channels/${id}`);
  },
  async moveToFolder(id, folderId) {
    return (await axios.put(`${BASE}/channels/${id}/folder`, { folderId })).data;
  },
  async createFolder(name) {
    return (await axios.post(`${BASE}/folders`, { name })).data;
  },
  async renameFolder(id, name) {
    return (await axios.patch(`${BASE}/folders/${id}`, { name })).data;
  },
  async reorderFolders(ids) {
    return (await axios.post(`${BASE}/folders/reorder`, { ids })).data;
  },
  async deleteFolder(id) {
    await axios.delete(`${BASE}/folders/${id}`);
  },
  async linkUploadChannels() {
    return (await axios.post(`${BASE}/upload-channels`)).data;
  },
  async createConnectedChannel(proxy, password) {
    return (await axios.post(`${BASE}/connections`, { proxy, password })).data;
  },
  async connection(id) {
    return (await axios.get(`${BASE}/channels/${id}/connection`)).data;
  },
  async saveConnection(id, proxy, password) {
    return (await axios.put(`${BASE}/channels/${id}/connection`, { proxy, password })).data;
  },
  async connectionLink(id) {
    return (await axios.post(`${BASE}/channels/${id}/connection/link`)).data;
  },
  async createImportChannel(name, youtubeChannelId) {
    return (await axios.post(`${BASE}/imports`, { name, youtubeChannelId })).data;
  },
  async importExport(id, file) {
    return (
      await axios.post(`${BASE}/channels/${id}/import`, file, {
        headers: { 'Content-Type': 'application/zip' },
      })
    ).data;
  },
  async syncStatus() {
    return (await axios.get(`${BASE}/sync`)).data;
  },
  async syncNow() {
    return (await axios.post(`${BASE}/sync`)).data;
  },
};

export default AnalyticsService;
